// @oasis-permission-inherits: /projects (projects:view)
import { createFileRoute } from "@tanstack/react-router";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { getApiError } from "~/lib/network/errors";
import ResponsiveGrid from "~/modules/mirage/components/responsive-grid";
import { animationLibraryBasePlayback } from "~/modules/projects/animation-library/base-playback";
import {
  buildFxCompositionRenderFromFx,
  formatDurationMs,
} from "~/modules/projects/animation-library/fx-composition-render";
import {
  listAnimationLibraryQuery,
  type AnimationLibraryItem,
} from "~/modules/projects/animation-library/network";
import { JerboaProjectPreview } from "~/modules/projects/components/jerboa-project-preview";

export const Route = createFileRoute("/_dash/projects/animation-library")({
  component: RouteComponent,
});

const animationLibraryPreviewDurationMs = animationLibraryBasePlayback.stats.durationMs ?? 5000;

function RouteComponent() {
  const { data, error, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    listAnimationLibraryQuery.useInfiniteQuery({ params: {} });
  const items = data?.pages.flatMap((page) => page.page) ?? [];

  return (
    <div className="space-y-6 pb-12">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="max-w-3xl space-y-1">
          <h1 className="text-xl font-bold">Animation Library</h1>
          <p className="text-sm text-muted-foreground">
            Public FX compositions from the Camel animation library, rendered directly through
            Jerboa from the returned datamodel.
          </p>
        </div>
        <Badge variant="outline">{items.length} loaded</Badge>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {error instanceof Error
            ? (getApiError(error)?.message ?? error.message)
            : "Failed to load animation library"}
        </div>
      )}

      <ResponsiveGrid gap={16} maxColumns={3} itemMinWidth={320} itemMinHeight={620}>
        {items.map((item) => (
          <AnimationLibraryCard key={`${item.fx.id}-${item.previewAsset}`} item={item} />
        ))}
        {(isLoading || isFetchingNextPage) &&
          Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="min-h-[620px] w-full rounded-lg" />
          ))}
      </ResponsiveGrid>

      {!isLoading && !error && items.length === 0 && (
        <div className="rounded-lg border bg-card p-6 text-sm text-muted-foreground">
          No animation library compositions found.
        </div>
      )}

      {hasNextPage && (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="outline"
            disabled={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
          >
            {isFetchingNextPage ? "Loading..." : "Load more animations"}
          </Button>
        </div>
      )}
    </div>
  );
}

function AnimationLibraryCard({ item }: { item: AnimationLibraryItem }) {
  const renderResult = buildAnimationLibraryRender(item);
  const animatorCount = item.fx.dynamics?.entries?.length ?? 0;
  const actionCount = renderResult.render?.actions.length ?? 0;
  const durationMs = renderResult.render?.trackItem.duration ?? animationLibraryPreviewDurationMs;

  return (
    <article className="min-w-0 overflow-hidden rounded-lg border bg-card shadow-xs">
      <div className="space-y-3 border-b bg-muted/30 p-4">
        <div className="space-y-1">
          <h2 className="text-base font-semibold">{item.fx.name}</h2>
          <p className="break-all font-mono text-xs text-muted-foreground">{item.fx.id}</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Badge variant="secondary">{item.fx.layers.length} root layers</Badge>
          <Badge variant="secondary">{animatorCount} animators</Badge>
          <Badge variant="secondary">{actionCount} direct action</Badge>
          <Badge variant="outline">{formatDurationMs(durationMs)}</Badge>
        </div>
      </div>

      <div className="space-y-3 p-3">
        {renderResult.render ? (
          <JerboaProjectPreview
            playback={animationLibraryBasePlayback}
            isLoading={false}
            playbackError={null}
            projectActions={renderResult.render.actions}
            autoPlay
            loop
            manualClock
          />
        ) : (
          <div className="grid aspect-[9/16] w-full place-items-center rounded-md border border-destructive/40 bg-destructive/10 p-4 text-center text-sm text-destructive">
            {renderResult.error}
          </div>
        )}
        <div className="rounded-md border bg-muted/20 p-3">
          <p className="text-xs font-medium text-muted-foreground">Library preview asset</p>
          <p className="mt-1 break-all font-mono text-[11px]">{item.previewAsset}</p>
        </div>
      </div>

      {renderResult.render && (
        <details className="border-t bg-muted/20 p-4 text-xs">
          <summary className="cursor-pointer select-none font-medium text-foreground">
            Direct FX composition insert action
          </summary>
          <pre className="mt-3 max-h-96 overflow-auto rounded-md border bg-background p-3 text-[11px] leading-4 text-muted-foreground">
            {JSON.stringify(renderResult.render.actions, null, 2)}
          </pre>
        </details>
      )}

      <details className="border-t bg-muted/20 p-4 text-xs">
        <summary className="cursor-pointer select-none font-medium text-foreground">
          FX datamodel
        </summary>
        <pre className="mt-3 max-h-96 overflow-auto rounded-md border bg-background p-3 text-[11px] leading-4 text-muted-foreground">
          {JSON.stringify(item.fx, null, 2)}
        </pre>
      </details>
    </article>
  );
}

function buildAnimationLibraryRender(item: AnimationLibraryItem) {
  try {
    return {
      render: buildFxCompositionRenderFromFx(item.fx, animationLibraryPreviewDurationMs),
      error: null,
    };
  } catch (cause) {
    return {
      render: null,
      error: cause instanceof Error ? cause.message : String(cause),
    };
  }
}
